import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
  ShieldCheck, Plus, Edit2, Trash2, X, Users, Key, Building2,
  Eye, EyeOff, UploadCloud, FileSpreadsheet, CheckCircle2,
  AlertCircle, Loader2, RefreshCw, Trash, BookOpen, Download, FileText, UserCog, History, AlertTriangle, ScanEye, Wrench
} from "lucide-react";
import PersonnelSyncTab from "./PersonnelSyncTab";
import ContractCatalogsTab from "./ContractCatalogsTab";
import AuditLogTab from "./AuditLogTab";
import BulkImportTab from "./BulkImportTab";
import NonCanonicalTab from "./NonCanonicalTab";
import OcrStatsTab from "./OcrStatsTab";
import WorkshopSettingsTab from "./WorkshopSettingsTab";

const ROLES = [
  { value: "admin", label: "Administrador", description: "Acceso total a la plataforma", color: "bg-red-50 text-red-700" },
  { value: "manager", label: "Responsable", description: "Gestión de contratos asignados", color: "bg-amber-50 text-amber-700" },
  { value: "operator", label: "Operario", description: "Entrada de partes de trabajo", color: "bg-blue-50 text-blue-600" },
  { value: "viewer", label: "Visualizador", description: "Solo lectura de informes", color: "bg-slate-100 text-slate-600" },
];

const roleBadge = (role) => {
  const r = ROLES.find(r => r.value === role) || { label: role, color: "bg-slate-100 text-slate-600" };
  return <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${r.color}`}>{r.label}</span>;
};

const emptyForm = { email: "", password: "", name: "", role: "operator", contract_ids: [], active: true };

// ── Manual Tab ────────────────────────────────────────────────────────────────
function ManualTab() {
  const { authAxios } = useAuth();
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingHtml, setLoadingHtml] = useState(false);
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const handleDownload = async (type) => {
    const isHtml = type === "html";
    if (isHtml) setLoadingHtml(true); else setLoadingPdf(true);
    try {
      const response = await authAxios.get(`/admin/manual/${type}`, { responseType: "blob" });
      const blob = new Blob([response.data], {
        type: isHtml ? "text/html" : "application/pdf"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = isHtml ? "Manual_Valoriza.html" : "Manual_Valoriza.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Error al descargar el manual. Inténtalo de nuevo.");
    } finally {
      if (isHtml) setLoadingHtml(false); else setLoadingPdf(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-in-up">
      <div className="card-white p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#E6F4EA] rounded-xl flex items-center justify-center">
            <BookOpen size={20} className="text-[#007934]" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800">Manual de Usuario</h3>
            <p className="text-sm text-slate-400">Guía completa de la plataforma Valoriza — Versión 1.0</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-6 leading-relaxed">
          El manual incluye instrucciones detalladas para todos los módulos: acceso, dashboard, planificación, 
          partes de trabajo, importación con IA, informes, análisis de anomalías y administración.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* PDF Download */}
          <div className="border border-slate-200 rounded-xl p-5 hover:border-[#007934]/30 hover:bg-[#E6F4EA]/30 transition-all">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center">
                <FileText size={17} className="text-red-500" />
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm">Formato PDF</p>
                <p className="text-xs text-slate-400">Ideal para imprimir y compartir</p>
              </div>
            </div>
            <button
              data-testid="download-manual-pdf-btn"
              onClick={() => handleDownload("pdf")}
              disabled={loadingPdf}
              className="w-full flex items-center justify-center gap-2 bg-[#007934] hover:bg-[#005c27] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95 disabled:opacity-60"
            >
              {loadingPdf ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              {loadingPdf ? "Generando PDF..." : "Descargar PDF"}
            </button>
          </div>
          {/* HTML Download */}
          <div className="border border-slate-200 rounded-xl p-5 hover:border-[#007934]/30 hover:bg-[#E6F4EA]/30 transition-all">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center">
                <FileText size={17} className="text-blue-500" />
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm">Formato HTML</p>
                <p className="text-xs text-slate-400">Visualización online, impresión desde navegador</p>
              </div>
            </div>
            <button
              data-testid="download-manual-html-btn"
              onClick={() => handleDownload("html")}
              disabled={loadingHtml}
              className="w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95 disabled:opacity-60"
            >
              {loadingHtml ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              {loadingHtml ? "Preparando..." : "Descargar HTML"}
            </button>
          </div>
        </div>
      </div>
      {/* Tip */}
      <div className="bg-[#E6F4EA] border border-[#007934]/20 rounded-xl p-4 text-sm text-[#005c27]">
        <p className="font-semibold mb-1">Consejos de uso</p>
        <ul className="list-disc list-inside space-y-1 text-xs text-[#007934]">
          <li>El <strong>PDF</strong> se genera en el servidor e incluye todo el contenido listo para imprimir o distribuir</li>
          <li>El <strong>HTML</strong> puede abrirse en cualquier navegador — usa Ctrl+P para imprimir como PDF con diseño completo</li>
        </ul>
      </div>
    </div>
  );
}

// ── Service Plans Tab ─────────────────────────────────────────────────────────
function ServicePlansTab({ contracts }) {
  const { authAxios } = useAuth();
  const fileRef = useRef(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadContract, setUploadContract] = useState(contracts[0]?.id || "");
  const [uploadYear, setUploadYear] = useState(2026);
  const [dragging, setDragging] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (contracts.length > 0 && !uploadContract) setUploadContract(contracts[0].id);
  }, [contracts]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/admin/service-plans");
      setPlans(r.data);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { fetchPlans(); }, []);

  const handleUpload = async (file) => {
    if (!uploadContract || !file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsm", "xlsx", "xls"].includes(ext)) {
      alert("Formato no soportado. Usa un archivo Excel (.xlsm, .xlsx).");
      return;
    }
    setUploading(true);
    setLastResult(null);
    try {
      const form = new FormData();
      form.append("contract_id", uploadContract);
      form.append("year", uploadYear);
      form.append("file", file);
      const r = await authAxios.post("/admin/service-plans/import", form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setLastResult({ ok: true, ...r.data });
      await fetchPlans();
    } catch (e) {
      setLastResult({ ok: false, error: e.response?.data?.detail || "Error importando" });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (contractId, year) => {
    if (!window.confirm(`¿Eliminar el plan económico ${year} de este contrato?`)) return;
    try {
      await authAxios.delete(`/admin/service-plans/${contractId}?year=${year}`);
      setPlans(prev => prev.filter(p => !(p.contract_id === contractId && p.year === year)));
    } catch (e) {
      alert(e.response?.data?.detail || "Error al eliminar");
    }
  };

  const contractName = (id) => contracts.find(c => c.id === id)?.name || id;
  const contractColor = (id) => contracts.find(c => c.id === id)?.color || "#007934";

  return (
    <div className="space-y-5">
      {/* Upload area */}
      <div className="card-white p-5">
        <h3 className="font-bold text-slate-800 text-sm mb-4 flex items-center gap-2">
          <UploadCloud size={15} className="text-[#007934]" /> Importar Condiciones Económicas
        </h3>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-500 whitespace-nowrap">Contrato:</label>
            <select
              data-testid="plan-contract-select"
              value={uploadContract}
              onChange={e => setUploadContract(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20"
            >
              {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-500 whitespace-nowrap">Año:</label>
            <input
              data-testid="plan-year-input"
              type="number"
              value={uploadYear}
              onChange={e => setUploadYear(parseInt(e.target.value))}
              min={2020}
              max={2035}
              className="w-20 text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20"
            />
          </div>
        </div>

        <div
          data-testid="plan-upload-dropzone"
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]); }}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
            ${dragging ? "border-[#007934] bg-[#E6F4EA]" : "border-slate-200 hover:border-[#007934]/40 hover:bg-slate-50"}
          `}
        >
          {uploading ? (
            <>
              <Loader2 size={28} className="mx-auto mb-3 text-[#007934] animate-spin" />
              <p className="font-semibold text-slate-600 text-sm">Procesando Excel...</p>
            </>
          ) : (
            <>
              <FileSpreadsheet size={28} className={`mx-auto mb-3 ${dragging ? "text-[#007934]" : "text-slate-300"}`} />
              <p className="font-semibold text-slate-600 text-sm">Arrastra el Excel de condiciones económicas</p>
              <p className="text-xs text-slate-400 mt-1">
                Formato esperado: hoja <strong>DIM_SERVICIO_puestos_plantilla</strong> + <strong>CD_PERSONAL</strong>
              </p>
            </>
          )}
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls" className="hidden" onChange={e => { if (e.target.files[0]) handleUpload(e.target.files[0]); e.target.value = ""; }} />
        </div>

        {/* Upload result */}
        {lastResult && (
          <div className={`mt-4 p-4 rounded-xl flex items-start gap-3 ${lastResult.ok ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-100"}`}>
            {lastResult.ok
              ? <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              : <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />}
            <div>
              {lastResult.ok ? (
                <>
                  <p className="font-semibold text-emerald-800 text-sm">¡Importado correctamente!</p>
                  <p className="text-xs text-emerald-700 mt-0.5">
                    {lastResult.services_count} servicios · {lastResult.unit_costs_count} categorías de coste
                  </p>
                </>
              ) : (
                <p className="text-sm text-red-600">{lastResult.error}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Plans list */}
      <div className="card-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
            <FileSpreadsheet size={15} className="text-slate-500" /> Planes Económicos Importados
          </h3>
          <button onClick={fetchPlans} className="text-slate-400 hover:text-slate-600 transition-colors">
            <RefreshCw size={14} />
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-8 h-8 border-4 border-[#007934] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-10 text-slate-400">
            <FileSpreadsheet size={28} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No hay planes económicos importados</p>
          </div>
        ) : (
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Contrato</th>
                <th>Año</th>
                <th>Servicios</th>
                <th>Fichero</th>
                <th>Importado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p, i) => (
                <tr key={i} data-testid={`plan-row-${p.contract_id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: contractColor(p.contract_id) }} />
                      <span className="font-semibold text-slate-800 text-sm">{contractName(p.contract_id)}</span>
                    </div>
                  </td>
                  <td><span className="font-bold text-slate-700">{p.year}</span></td>
                  <td>
                    <span className="text-xs bg-[#E6F4EA] text-[#007934] font-semibold px-2 py-0.5 rounded-full">
                      {p.services_count || "—"} servicios
                    </span>
                  </td>
                  <td className="text-slate-500 text-xs max-w-[200px]">
                    <p className="truncate" title={p.filename}>{p.filename}</p>
                  </td>
                  <td className="text-xs text-slate-400">
                    {p.uploaded_at ? new Date(p.uploaded_at).toLocaleDateString("es-ES") : "—"}
                  </td>
                  <td>
                    <button
                      data-testid={`delete-plan-${p.contract_id}`}
                      onClick={() => handleDelete(p.contract_id, p.year)}
                      className="text-slate-400 hover:text-red-400 transition-colors"
                    >
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Format guide */}
      <div className="card-white p-5">
        <h3 className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-2">
          <AlertCircle size={14} className="text-amber-500" /> Formato esperado del Excel
        </h3>
        <div className="space-y-2 text-xs text-slate-600">
          <p><strong>Hoja obligatoria:</strong> <code className="bg-slate-100 px-1.5 py-0.5 rounded">DIM_SERVICIO_puestos_plantilla</code> — catálogo de servicios con turnos y plantilla</p>
          <p><strong>Hoja recomendada:</strong> <code className="bg-slate-100 px-1.5 py-0.5 rounded">CD_PERSONAL</code> — costes unitarios por categoría y turno (€/jornada)</p>
          <p className="text-slate-400">El formato es el mismo para todos los contratos. Si el Excel de otro contrato tiene estas hojas con la misma estructura, funcionará automáticamente.</p>
        </div>
      </div>
    </div>
  );
}

// ── Main Admin Component ──────────────────────────────────────────────────────
export default function Admin() {
  const { authAxios, user } = useAuth();
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      authAxios.get("/admin/users"),
      authAxios.get("/contracts")
    ]).then(([u, c]) => {
      setUsers(u.data);
      setContracts(c.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openNew = () => {
    setEditUser(null);
    setForm(emptyForm);
    setError("");
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ email: u.email, password: "", name: u.name, role: u.role, contract_ids: u.contract_ids || [], active: u.active !== false });
    setError("");
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.email || !form.name) { setError("Email y nombre son obligatorios"); return; }
    if (!editUser && !form.password) { setError("La contraseña es obligatoria para nuevos usuarios"); return; }
    setSaving(true);
    setError("");
    try {
      if (editUser) {
        const payload = { name: form.name, role: form.role, contract_ids: form.contract_ids, active: form.active };
        if (form.password) payload.password = form.password;
        const r = await authAxios.put(`/admin/users/${editUser.id}`, payload);
        setUsers(prev => prev.map(u => u.id === editUser.id ? r.data : u));
      } else {
        const r = await authAxios.post("/admin/users", form);
        setUsers(prev => [...prev, r.data]);
      }
      setShowModal(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Error al guardar el usuario");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("¿Eliminar este usuario? Esta acción no se puede deshacer.")) return;
    try {
      await authAxios.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (e) {
      alert(e.response?.data?.detail || "Error al eliminar");
    }
  };

  const toggleContract = (contractId) => {
    setForm(f => ({
      ...f,
      contract_ids: f.contract_ids.includes(contractId)
        ? f.contract_ids.filter(id => id !== contractId)
        : [...f.contract_ids, contractId]
    }));
  };

  const contractName = (id) => contracts.find(c => c.id === id)?.name || id;
  const contractColor = (id) => contracts.find(c => c.id === id)?.color || "#007934";

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="w-10 h-10 border-4 border-[#007934] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={20} className="text-[#007934]" />
            <h1 className="text-2xl font-black text-slate-900">Administración</h1>
          </div>
          <p className="text-slate-400 text-sm">{users.length} usuarios · {contracts.length} contratos</p>
        </div>
        {activeTab === "users" && (
          <button
            data-testid="add-user-btn"
            onClick={openNew}
            className="flex items-center gap-2 bg-[#007934] hover:bg-[#005c27] text-white px-4 py-2.5 rounded-xl font-semibold text-sm shadow-md transition-all active:scale-95"
          >
            <Plus size={16} /> Nuevo Usuario
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit animate-fade-in-up">
        {[
          { id: "users", label: "Usuarios", icon: Users },
          { id: "service-plans", label: "Catálogo de Servicios", icon: FileSpreadsheet },
          { id: "catalogs", label: "Catálogos por Contrato", icon: Building2 },
          { id: "personnel-sync", label: "Sincronización Personal", icon: UserCog },
          { id: "non-canonical", label: "Valores no canónicos", icon: AlertTriangle },
          { id: "audit", label: "Auditoría", icon: History },
          { id: "ocr-stats", label: "Calidad OCR", icon: ScanEye },
          { id: "workshop-settings", label: "Taller", icon: Wrench },
          { id: "manual", label: "Manual de Usuario", icon: BookOpen },
          { id: "bulk-import", label: "Importación masiva", icon: UploadCloud },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Users tab */}
      {activeTab === "users" && (
        <>
          {/* Role summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 animate-fade-in-up">
            {ROLES.map(r => {
              const count = users.filter(u => u.role === r.value).length;
              return (
                <div key={r.value} className="card-white p-4 border-l-4 border-slate-200">
                  <p className="text-xs font-semibold text-slate-400 truncate">{r.label}</p>
                  <p className="text-2xl font-black text-slate-800 mt-1">{count}</p>
                  <p className="text-xs text-slate-400 mt-0.5 leading-tight">{r.description}</p>
                </div>
              );
            })}
          </div>

      {/* Users table */}
      <div className="card-white overflow-hidden animate-fade-in-up">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800 text-sm flex items-center gap-2">
            <Users size={16} className="text-slate-500" /> Usuarios de la Plataforma
          </h2>
        </div>
        <table className="w-full data-table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Rol</th>
              <th>Contratos Asignados</th>
              <th>Estado</th>
              <th>Creado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[#007934] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                      {u.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{u.name}</p>
                      <p className="text-xs text-slate-400">{u.email}</p>
                    </div>
                    {u.id === user?.id && (
                      <span className="text-[10px] bg-[#E6F4EA] text-[#007934] font-semibold px-2 py-0.5 rounded-full">Tú</span>
                    )}
                  </div>
                </td>
                <td>{roleBadge(u.role)}</td>
                <td>
                  {u.role === "admin" ? (
                    <span className="text-xs text-slate-400 italic">Todos los contratos</span>
                  ) : u.contract_ids?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {u.contract_ids.map(id => (
                        <span key={id} className="text-xs px-2 py-0.5 rounded-full font-medium text-white" style={{ backgroundColor: contractColor(id) }}>
                          {contractName(id)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-amber-500 font-medium">Sin contratos</span>
                  )}
                </td>
                <td>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.active !== false ? "badge-ok" : "badge-draft"}`}>
                    {u.active !== false ? "Activo" : "Inactivo"}
                  </span>
                </td>
                <td className="text-xs text-slate-400">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString("es-ES") : "—"}
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`edit-user-${u.id}`}
                      onClick={() => openEdit(u)}
                      className="text-slate-400 hover:text-[#007934] transition-colors"
                    >
                      <Edit2 size={14} />
                    </button>
                    {u.id !== user?.id && (
                      <button
                        data-testid={`delete-user-${u.id}`}
                        onClick={() => handleDelete(u.id)}
                        className="text-slate-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Users size={32} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">No hay usuarios registrados</p>
          </div>
        )}
      </div>

      {/* Roles info */}
      <div className="card-white p-5 animate-fade-in-up">
        <h3 className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-2">
          <Key size={15} className="text-slate-500" /> Descripción de Roles
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ROLES.map(r => (
            <div key={r.value} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50">
              <span className={`text-xs px-2.5 py-1 rounded-full font-semibold flex-shrink-0 ${r.color}`}>{r.label}</span>
              <p className="text-xs text-slate-600 leading-relaxed">{r.description}</p>
            </div>
          ))}
        </div>
      </div>
        </>
      )}

      {/* Service Plans tab */}
      {activeTab === "service-plans" && (
        <ServicePlansTab contracts={contracts} />
      )}

      {/* Catalogs tab */}
      {activeTab === "catalogs" && (
        <ContractCatalogsTab contracts={contracts} />
      )}

      {/* Personnel Sync tab */}
      {activeTab === "personnel-sync" && (
        <PersonnelSyncTab contracts={contracts} />
      )}

      {/* Audit Log tab */}
      {activeTab === "audit" && (
        <AuditLogTab contracts={contracts} />
      )}

      {/* OCR Quality Stats tab */}
      {activeTab === "ocr-stats" && (
        <OcrStatsTab />
      )}

      {/* Workshop settings tab */}
      {activeTab === "workshop-settings" && (
        <WorkshopSettingsTab />
      )}

      {/* Non-canonical values tab */}
      {activeTab === "non-canonical" && (
        <NonCanonicalTab contracts={contracts} />
      )}

      {/* Manual tab */}
      {activeTab === "manual" && (
        <ManualTab />
      )}

      {/* Bulk Import tab */}
      {activeTab === "bulk-import" && (
        <BulkImportTab />
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 animate-scale-in max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-900">{editUser ? "Editar Usuario" : "Nuevo Usuario"}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Nombre completo</label>
                <input
                  data-testid="user-name-input"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/20"
                  placeholder="Admin Valoriza"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</label>
                <input
                  data-testid="user-email-input"
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  disabled={!!editUser}
                  className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/20 disabled:bg-slate-50 disabled:text-slate-400"
                  placeholder="usuario@valoriza.com"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Contraseña {editUser && <span className="text-slate-300 font-normal">(dejar vacío para no cambiar)</span>}
                </label>
                <div className="relative mt-1">
                  <input
                    data-testid="user-password-input"
                    type={showPass ? "text" : "password"}
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/20"
                    placeholder={editUser ? "••••••••" : "Mínimo 8 caracteres"}
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Rol</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {ROLES.map(r => (
                    <button
                      key={r.value}
                      type="button"
                      data-testid={`role-${r.value}`}
                      onClick={() => setForm(f => ({ ...f, role: r.value }))}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${form.role === r.value ? "border-[#007934] bg-[#E6F4EA]" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      <p className="text-sm font-semibold text-slate-800">{r.label}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{r.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {form.role !== "admin" && (
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">
                    <Building2 size={12} /> Contratos Asignados
                  </label>
                  <div className="mt-2 space-y-2">
                    {contracts.map(c => (
                      <label key={c.id} className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-slate-50">
                        <input
                          type="checkbox"
                          data-testid={`contract-check-${c.id}`}
                          checked={form.contract_ids.includes(c.id)}
                          onChange={() => toggleContract(c.id)}
                          className="rounded"
                        />
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color || "#007934" }} />
                        <span className="text-sm text-slate-700">{c.name}</span>
                        <span className="text-xs text-slate-400">{c.city}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {editUser && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="user-active"
                    data-testid="user-active-toggle"
                    checked={form.active}
                    onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                    className="rounded"
                  />
                  <label htmlFor="user-active" className="text-sm text-slate-600">Usuario activo</label>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
                Cancelar
              </button>
              <button
                data-testid="save-user-btn"
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 text-sm font-semibold bg-[#007934] text-white rounded-lg hover:bg-[#005c27] shadow-md transition-all disabled:opacity-60"
              >
                {saving ? "Guardando..." : editUser ? "Actualizar" : "Crear Usuario"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
